import firebase from "../config/firebase";

export type Quote = {
  from_code: string;
  to_code: string;
  eta?: string; // "YYYY-MM-DD"
  etd?: string; // "YYYY-MM-DD"
  from_to_eta?: string;
  from_to_etd?: string;
  [key: string]: any;
};

export type QuoteWithId = Quote & { id: string };

const compose = (fromCode: string, toCode: string, date: string) =>
  `${fromCode}_${toCode}_${date}`;

class QuotesFirebaseDataService {
  private db: any;

  constructor() {
    this.db = firebase.ref("/quotes");
  }

  getAll(): any {
    const userId = localStorage.getItem("user_id");
    if (!userId) return [];
    return this.db;
  }

  // ===== Queries por RANGE =====
  getByFromToEtaRangeRef(fromCode: string, toCode: string, startDate: string, endDate: string) {
    const startKey = compose(fromCode, toCode, startDate);
    const endKey = compose(fromCode, toCode, endDate);
    console.log(`Buscando (ETA) de ${fromCode} para ${toCode} entre ${startDate} e ${endDate}`);
    return this.db.orderByChild("from_to_eta").startAt(startKey).endAt(endKey);
  }

  getByFromToEtdRangeRef(fromCode: string, toCode: string, startDate: string, endDate: string) {
    const startKey = compose(fromCode, toCode, startDate);
    const endKey = compose(fromCode, toCode, endDate);
    console.log(`Buscando (ETD) de ${fromCode} para ${toCode} entre ${startDate} e ${endDate}`);
    return this.db.orderByChild("from_to_etd").startAt(startKey).endAt(endKey);
  }

  getByFromToEtaRangeRefImported(fromCode: string, toCode: string, startDate: string, endDate: string) {
    const startKey = compose(fromCode, toCode, startDate);
    const endKey = compose(fromCode, toCode, endDate);
    console.log(`Buscando (ETA Importado) de ${fromCode} para ${toCode} entre ${startDate} e ${endDate}`);
    return this.db.orderByChild("from_import_to_eta").startAt(startKey).endAt(endKey);
  }

  /**
   * Pega automaticamente a REF que tem resultado (prefere ETA; se vazio, usa ETD).
   * Retorna uma referência para você anexar listeners (.on).
   */
  async getRangeAutoRef(
    fromCode: string,
    toCode: string,
    startDate: string,
    endDate: string
  ): Promise<any> {
    const etaRef = this.getByFromToEtaRangeRef(fromCode, toCode, startDate, endDate);
    const etaSnap = await etaRef.limitToFirst(1).once("value");
    if (etaSnap.exists()) {
      console.log("AutoRef: usando índice from_to_eta");
      return etaRef;
    }
    const etdRef = this.getByFromToEtdRangeRef(fromCode, toCode, startDate, endDate);
    const etdSnap = await etdRef.limitToFirst(1).once("value");
    if (etdSnap.exists()) {
      console.log("AutoRef: usando índice from_to_etd");
      return etdRef;
    }
    console.warn("AutoRef: nenhum resultado para ETA nem ETD neste range.");
    return etaRef; // default
  }

  // ===== CRUD =====
  create(data: Quote): Promise<any> {
    const payload: Quote = { ...data };
    if (payload.from_code && payload.to_code && payload.eta) {
      payload.from_to_eta = compose(payload.from_code, payload.to_code, payload.eta);
    }
    if (payload.from_code && payload.to_code && payload.etd) {
      payload.from_to_etd = compose(payload.from_code, payload.to_code, payload.etd);
    }
    return this.db.push(payload); // grava já com as chaves compostas
  }

  async update(key: string, value: Partial<Quote>): Promise<void> {
    const snap = await this.db.child(key).once("value");
    const curr: Quote = snap.val() || ({} as Quote);
    const next: Quote = { ...curr, ...value };

    if (next.from_code && next.to_code && next.eta) {
      next.from_to_eta = compose(next.from_code, next.to_code, next.eta);
    }
    if (next.from_code && next.to_code && next.etd) {
      next.from_to_etd = compose(next.from_code, next.to_code, next.etd);
    }

    await this.db.child(key).update(next);
  }

  delete(key: string): Promise<void> {
    return this.db.child(key).remove();
  }

  deleteAll(): Promise<void> {
    return this.db.remove();
  }

  // ===== Utilitários =====
  /** Preenche from_to_eta / from_to_etd onde faltam (rode 1x se necessário) */
  async backfillCompositeKeys(): Promise<{ updated: number }> {
    const snap = await this.db.once("value");
    const data: Record<string, Quote> = snap.val() || {};
    const updates: Record<string, string> = {};

    for (const [id, item] of Object.entries(data)) {
      if (!item) continue;
      if (item.from_code && item.to_code && item.eta && !item.from_to_eta) {
        updates[`/${id}/from_to_eta`] = compose(item.from_code, item.to_code, item.eta);
      }
      if (item.from_code && item.to_code && item.etd && !item.from_to_etd) {
        updates[`/${id}/from_to_etd`] = compose(item.from_code, item.to_code, item.etd);
      }
    }

    const count = Object.keys(updates).length;
    if (count > 0) await this.db.update(updates);
    console.log(`Backfill: atualizados ${count} registros`);
    return { updated: count };
  }

  async debugSample(limit = 5): Promise<void> {
    const s = await this.db.limitToFirst(limit).once("value");
    console.log("Sample /quotes:", s.val());
  }
}

export default new QuotesFirebaseDataService();
